import { computed, inject, Injectable, Signal, untracked } from '@angular/core';
import { ComponentStore, tapResponse } from '@ngrx/component-store';
import {
    createGeoKey,
    createMcEventNames,
    createMciForecastKey,
    createMciKey,
    McCondition,
    McQuery,
    McResponseMci,
    McResponseMciSchema,
} from './iq-mc-event-bus-state.models';
import {
    IqEventBusBrokerService,
    IqEventBusBrokerHandler,
} from '@dat/iq-event-bus';

import { get, isEmpty, isNil, set, sortBy } from 'lodash';
import { filter, Observable } from 'rxjs';
import { IqMcEventBusPluginGraphqlService } from './iq-mc-event-bus-plugin-graphql.service';
import { DateTime } from 'luxon';
import { isMciUpdating } from '@dat/iq-mc-utils';

type State = {
    /**
     * The key for the scores is a composite key of the equipment,
     * the geography area, and the direction.
     *
     * So the key for outbound market area would be
     *
     *
     *
     */
    conditions: Record<string, McCondition>;
    queries: Record<string, McQuery>;
};

@Injectable({
    providedIn: 'root',
})
export class IqMcEventBusStateMciService extends ComponentStore<State> {
    private _fetchKey: string = 'Mci';
    private _bus: IqEventBusBrokerService = inject(IqEventBusBrokerService);
    private _registerMap: Record<string, IqEventBusBrokerHandler> = {};
    private _plugin: IqMcEventBusPluginGraphqlService = inject(
        IqMcEventBusPluginGraphqlService,
    );
    private _queries: McQuery[] = [];

    constructor() {
        super({ conditions: {}, queries: {} });

        /**
         * Log the state of the store when it changes
         * This is used for debugging and monitoring
         */
        // effect(() => {
        //     console.log('Mci State Updated');
        //     console.log(this.state());
        // });

        this._registerMap = this._bus.registerMap({
            // type:mc-fetch:Mci
            [createMcEventNames(this._fetchKey)]: {
                callback: ({ query }: { query: McQuery }) => {
                    if (!query) return;
                    this.addQuery(query);
                    this.fetchMci(query);
                },
            },
            // type:mc-fetch:Mci:update-query
            [createMcEventNames(this._fetchKey, ['update-query'])]: {
                callback: ({ query }: { query: McQuery }) => {
                    if (!query) return;
                    this.patchQuery(query);
                    this.fetchMci(this.getQuery(query)());
                },
            },
        });
    }

    //#region Updaters
    addQuery = this.updater((state: State, query: McQuery) => {
        const id = query?.id ?? undefined;
        const currentQuery = get(state, ['queries', id], {});

        return !!id ? {
            ...state,
            queries: {
                ...state.queries,
                [id]: {
                    ...currentQuery,
                    ...query,
                },
            },
        } : state;
    });
    patchQuery = this.updater((state: State, query: Partial<McQuery>) => {
        const id = query?.id ?? undefined;
        const currentQuery = get(state, ['queries', id], {});

        if (isEmpty(currentQuery)) return state;

        return !!id ? {
            ...state,
            queries: {
                ...state.queries,
                [id]: {
                    ...currentQuery,
                    ...query,
                },
            },
        } : state;
    });

    setMciFetchArea = this.updater((state: State, query: McQuery) => {
        const conditionKey = createMciKey(
            query.equipmentType,
            query.geoType,
            query.direction,
            query.timeframe,
        );

        return this._setFetchArea(state, query, conditionKey);
    });

    setMciLoadArea = this.updater((state: State, query: McQuery) => {
        const conditionKey = createMciKey(
            query.equipmentType,
            query.geoType,
            query.direction,
            query.timeframe,
        );
        return this._setLoadedArea(state, conditionKey);
    });

    setMciForecastFetchArea = this.updater((state: State, query: McQuery) => {
        const forecastDates = [...Array(8)].map((_, i) =>
            DateTime.now()
                .setZone('America/Los_Angeles')
                .plus({ days: i })
                .toFormat('yyyy-MM-dd'),
        );
        const newState: State = forecastDates.reduce((acc, forecastDate) => {
            const conditionKey = createMciForecastKey(
                query.equipmentType,
                query.geoType,
                query.direction,
                query.timeframe,
                forecastDate,
            );
            return this._setFetchArea(acc, query, conditionKey);
        }, state);

        return newState;
    });

    setMciForecastLoadArea = this.updater((state: State, query: McQuery) => {
        const forecastDates = [...Array(8)].map((_, i) =>
            DateTime.now()
                .setZone('America/Los_Angeles')
                .plus({ days: i })
                .toFormat('yyyy-MM-dd'),
        );
        const newState: State = forecastDates.reduce((acc, forecastDate) => {
            const conditionKey = createMciForecastKey(
                query.equipmentType,
                query.geoType,
                query.direction,
                query.timeframe,
                forecastDate,
            );
            return this._setLoadedArea(acc, conditionKey);
        }, state);
        return newState;
    });

    setMciTimeline = this.updater((state: State, timeline: McResponseMci) => {
        const conditionKey = createMciKey(
            timeline.equipmentType,
            timeline.type,
            timeline.direction,
            timeline.timeframe,
        );
        const geoKey = timeline.code;
        const currentMci = state.conditions[conditionKey]?.Mci?.[geoKey] ?? {};
        const currentCondition = state.conditions[conditionKey] ?? {};
        const fetchInMs: number = Date.now();
        return {
            ...state,
            conditions: {
                ...state.conditions,
                [conditionKey]: {
                    ...currentCondition,
                    Mci: {
                        ...(state.conditions[conditionKey]?.Mci ?? {}),
                        [geoKey]: {
                            ...currentMci,
                            fetch: currentMci.fetch,
                            fetchInMs: currentMci.fetchInMs ?? fetchInMs,
                            timeline: timeline.timeline,
                        },
                    },
                },
            },
        };
    });

    setMciListForArea = this.updater(
        (state: State, MciList: McResponseMci[]) => {
            const newState: State = MciList.reduce((acc, Mci) => {
                const conditionKey = createMciKey(
                    Mci.equipmentType,
                    Mci.type,
                    Mci.direction,
                    Mci.timeframe,
                );
                return this._processMciRecord(acc, Mci, conditionKey);
            }, state);
            return newState;
        },
    );

    setMciListForForecast = this.updater(
        (state: State, MciList: McResponseMci[]) => {
            const newState: State = MciList.reduce((acc, Mci) => {
                const conditionKey = createMciForecastKey(
                    Mci.equipmentType,
                    Mci.type,
                    Mci.direction,
                    Mci.timeframe,
                    Mci.date,
                );

                return this._processMciRecord(acc, Mci, conditionKey);
            }, state);
            return newState;
        },
    );

    setMciToEmpty = this.updater((state: State, query: McQuery) => {
        const conditionKey = createMciKey(
            query.equipmentType,
            query.geoType,
            query.direction,
            query.timeframe,
        );
        const geoKey = query.geoCode;
        const currentMci = state.conditions[conditionKey]?.Mci?.[geoKey] ?? {};
        const loadedInMs: number = Date.now();

        return {
            ...state,
            conditions: {
                ...state.conditions,
                [conditionKey]: {
                    id: conditionKey,
                    Mci: {
                        ...(state.conditions[conditionKey]?.Mci ?? {}),
                        [geoKey]: {
                            ...currentMci,
                            hasError: true,
                            error: 'No Data',
                            loaded: true,
                            loadedInMs,
                            fetch: currentMci.fetch,
                            fetchInMs: currentMci.fetchInMs,
                            durationInMs: loadedInMs - currentMci.fetchInMs,
                        },
                    },
                },
            },
        };
    });

    clearMciState = this.updater(() => {
        return {
            conditions: {},
            queries: {},
        }
    });
    //#endregion

    //#region Effects

    initialize = this.effect(($trigger) => {
        return $trigger;
    });

    fetchMci = this.effect(($query: Observable<McQuery>) => {
        return $query.pipe(
            tapResponse({
                next: async (query: McQuery) => {
                    if (isMciUpdating()) {
                        this.fetchMciUpdatingList(query);
                        return;
                    }
                    //Fetch all MCI scores if area isn't already fetched
                    if (!this.isAreaFetched(query)()) {
                        this.setMciFetchArea(query);

                        const Mci: McResponseMci[] =
                            await this.__getAllMciScores(query);
                        if (query.timeframe === 'EIGHT_DAY_FORECAST') {
                            this.setMciForecastFetchArea(query);
                            this.setMciListForForecast(Mci);
                            this.setMciForecastLoadArea(query);
                        }
                        this.setMciListForArea(Mci);
                        this.setMciLoadArea(query);
                    }

                    //This second call is made with geoCode only when the timeline is required
                    if (this.isTimelineRequired(query)) {
                        this.fetchMciTimeline(query);
                        return;
                    }
                    this._queries.push(query);
                    const sendMciDataEvents = computed(() => {
                        if(this.isAreaLoaded(query)()) {
                            untracked(() => {
                                this._queries.forEach((query) => {
                                    this.sendMciEvent(query?.id);
                                });
                                this._queries = []
                            });
                        }
                    });
                    sendMciDataEvents();

                },
                error: (error) => {
                    console.error(error);
                },
            }),
            tapResponse({
                next: (query: McQuery) => {
                    this.sendMciEvent(query?.id);
                },
                error: (e) => {
                    console.error(e);
                },
            }),
        );
    });

    fetchMciTimeline = this.effect(($query: Observable<McQuery>) => {
        return $query.pipe(
            tapResponse({
                next: async (query: McQuery) => {
                    if (this.isTimelineFetched(query)()) return;
                    const timeline: McResponseMci =
                        await this._fetchTimeline(query);
                    this.setMciTimeline(timeline);
                    this.sendMciEvent(query?.id);
                },
                error: (error) => {
                    console.error(error);
                },
            }),
        );
    });

    fetchMciUpdatingList = this.effect(($query: Observable<McQuery>) => {
        return $query.pipe(
            tapResponse({
                next: async (query: McQuery) => {
                    this.setMciFetchArea(query);
                    this.setMciToEmpty(query);
                    this.setMciLoadArea(query);
                    this.sendMciEvent(query?.id);
                },
                error: (error) => {
                    console.error(error);
                },
            }),
        );
    });

    sendMciEvent = this.effect((queryId: Observable<string>) => {
        return queryId.pipe(
            filter((queryId) => !!queryId),
            filter((queryId) => !!this.getQueryById(queryId)()),
            filter((queryId) => {
                const query = this.getQueryById(queryId)();
                return query?.type === 'area';
            }),
            tapResponse({
                next: (queryId: string) => {
                    const query = this.getQueryById(queryId)();
                    const Mci = this.getMci(query);
                    let payload = {
                        query,
                        Mci,
                        MciResponse:
                            this.getMciForArea(query)()
                                ?.MciAreaResponseInfo ?? {},
                    };
                    // type:mc-fetch:Mci:data
                    this._bus.dispatch({
                        name: createMcEventNames(this._fetchKey, ['data']),
                        payload,
                    });
                },
                error: (error) => {
                    console.error(error);
                },
            }
            ),
        );
    });

    // sendMciEvent = this.effect((query: Observable<McQuery>) => {
    //     return query.pipe(
    //         filter((query) => query?.type === 'area'),
    //         tapResponse({
    //             next: (query: McQuery) => {
    //                 const queryList = this.queryList();
    //                 queryList.forEach((currentQuery) => {
    //                     if (currentQuery.id !== query.id) return;
    //                     const Mci = this.getMci(query);
    //                     let payload = {
    //                         query,
    //                         Mci,
    //                         MciResponse:
    //                             this.getMciForArea(query)()
    //                                 ?.MciAreaResponseInfo ?? {},
    //                     };
    //                     // type:mc-fetch:Mci:data
    //                     this._bus.dispatch({
    //                         name: createMcEventNames(this._fetchKey, ['data']),
    //                         payload,
    //                     });
    //                 });
    //             },
    //             error: (error) => {
    //                 console.error(error);
    //             },
    //         }),
    //     );
    // });

    //#endregion

    //#region Selectors

    //#region Select By Query

    getQuery = (query: McQuery): Signal<McQuery> =>
        this.selectSignal((state) => {
            return state.queries[query.id];
        });

    getMciByGeo = (query: McQuery): Signal<McResponseMci> =>
        this.selectSignal((state) => {
            const conditionKey = createMciKey(
                query.equipmentType,
                query.geoType,
                query.direction,
                query.timeframe,
            );
            const geoKey = query.geoCode;

            return state.conditions[conditionKey]?.Mci?.[geoKey];
        });

    getMciForecastByGeo = (query: McQuery): Signal<McResponseMci> =>
        this.selectSignal((state) => {
            const conditionKey = createMciForecastKey(
                query.equipmentType,
                query.geoType,
                query.direction,
                query.timeframe,
                query.forecastDate,
            );
            const geoKey = query.geoCode;

            return state.conditions[conditionKey]?.Mci?.[geoKey];
        });

    getMciForArea = (query: McQuery): Signal<McCondition> =>
        this.selectSignal((state) => {
            const conditionKey = createMciKey(
                query.equipmentType,
                query.geoType,
                query.direction,
                query.timeframe,
            );

            return state.conditions[conditionKey];
        });

    getMciMap = (query: McQuery): Signal<Record<string, McResponseMci>> =>
        this.selectSignal((state) => {
            const conditionKey = createMciKey(
                query.equipmentType,
                query.geoType,
                query.direction,
                query.timeframe,
            );
            const Mci = state.conditions[conditionKey]?.Mci ?? {};

            return Object.entries(Mci).reduce((acc, [key, value]) => {
                return set(acc, key, value);
            }, {});
        });

    getMciMapForForecast = (
        query: McQuery,
    ): Signal<Record<string, McResponseMci>> =>
        this.selectSignal((state) => {
            const conditionKey = createMciForecastKey(
                query.equipmentType,
                query.geoType,
                query.direction,
                query.timeframe,
                query.forecastDate,
            );
            const Mci = state.conditions[conditionKey]?.Mci ?? {};

            return Object.entries(Mci).reduce((acc, [key, value]) => {
                return set(acc, key, value);
            }, {});
        });

    isGeoFetched = (query: McQuery): Signal<boolean> =>
        this.selectSignal(this.getMciByGeo(query), (Mci: McResponseMci) => {
            return !!Mci?.fetch;
        });

    isAreaFetched = (query: McQuery): Signal<boolean> =>
        this.selectSignal(this.getMciForArea(query), (Mci: McCondition) => {
            return !!Mci?.MciAreaResponseInfo?.fetch;
        });
    
    isAreaLoaded = (query: McQuery): Signal<boolean> =>
        this.selectSignal(this.getMciForArea(query), (Mci: McCondition) => {
            return !!Mci?.MciAreaResponseInfo?.loaded;
        });

    isTimelineFetched = (query: McQuery): Signal<boolean> =>
        this.selectSignal(this.getMciByGeo(query), (Mci: McResponseMci) => {
            return !isEmpty(Mci?.timeline);
        });

    //#endregion

    //#region Select By Id
    getQueryById = (id: string): Signal<McQuery> =>
        this.selectSignal((state) => {
            return state.queries[id];
        });

    //#endregion

    readonly queryList: Signal<McQuery[]> = this.selectSignal<McQuery[]>(
        (state): McQuery[] => sortBy(Object.values(state.queries), 'id'),
    );

    readonly getMciByGeoById = (id: string): Signal<McResponseMci> =>
        this.selectSignal((state) => {
            const query = state.queries[id];
            if (!query) return null;

            const conditionKey = createMciKey(
                query.equipmentType,
                query.geoType,
                query.direction,
                query.timeframe,
            );
            const geoKey = createGeoKey(query.geoCode);

            return state.conditions[conditionKey]?.Mci?.[geoKey];
        });

    //#endregion

    //#region Helpers
    getMci(query: McQuery): Record<string, McResponseMci> {
        if (query.timeframe === 'EIGHT_DAY_FORECAST' && !isMciUpdating()) {
            const MciForecast: Record<string, McResponseMci> =
                this.getMciForecast(query);
            return MciForecast;
        }
        const Mci: Record<string, McResponseMci> = this.getMciMap(query)();
        return Mci;
    }

    getMciForecast(query: McQuery): Record<string, McResponseMci> {
        const MciForecast: Record<string, McResponseMci> =
            this.getMciMapForForecast(query)();
        if (!isEmpty(query.geoCode)) {
            /* The Forecast State looks like this
                VAN.MKT.OUT.EIGHT_DAY_FORECAST : { .... } ----> Contains Common Forecast Data among all dates such as the timeline
                VAN.MKT.OUT.EIGHT_DAY_FORECAST.2025-01-07 : { .... } ---> These Records here contain MCI data for that particular forecast date
                VAN.MKT.OUT.EIGHT_DAY_FORECAST.2025-01-08 : { .... }


                The Forecast Key without the date key contains common data for forecast, such as the timeline,
                The reason to have it is so that we don't populate all the keys with redundant data and rather have it in one common place


                In case we have a geography selection made, We fetch the Timeline record from the common key and append it with the data for that particular forecast date
            */
            const mciForGeography: McResponseMci = {
                ...this.getMciForecastByGeo(query)(),
                timeline: this.getMciByGeo(query)()?.timeline,
            };
            return { ...MciForecast, [query.geoCode]: mciForGeography };
        }
        return MciForecast;
    }

    isTimelineRequired(query: McQuery): boolean {
        if(isNil(query) || isNil(query?.geoCode)) return false;
        return (
            !!query.geoCode?.length &&
            (query?.timeframe === 'THIRTY_DAYS_BACK' ||
                query?.timeframe === 'EIGHT_DAYS_BACK' ||
                query?.timeframe === 'EIGHT_DAY_FORECAST')
        );
    }

    private _fetchTimeline = async (
        query: McQuery,
    ): Promise<Partial<McResponseMci>> => {
        const timeline = await this._plugin.getMciTimeline(query);
        return timeline?.[0];
    };

    private async __getAllMciScores(query: McQuery): Promise<McResponseMci[]> {
        const queryWithoutGeography = {
            ...query,
            geoCode: null,
            displayName: null,
        };

        return await this._plugin.getMci(queryWithoutGeography);
    }

    private _processMciRecord(
        state: State,
        Mci: McResponseMci,
        conditionKey: string,
    ): State {
        const geoKey = Mci.code;
        const currentMci = state.conditions[conditionKey]?.Mci?.[geoKey] ?? {};
        const currentCondition = state.conditions[conditionKey] ?? {};
        const responseInfo = currentCondition.MciAreaResponseInfo ?? {};

        return {
            ...state,
            conditions: {
                ...state.conditions,
                [conditionKey]: {
                    ...currentCondition,
                    id: conditionKey,
                    Mci: {
                        ...(state.conditions[conditionKey]?.Mci ?? {}),
                        [geoKey]: {
                            ...currentMci,
                            ...Mci,
                            fetch: true,
                            fetchInMs: responseInfo.fetchInMs,
                            durationInMs:
                                Mci.loadedInMs - responseInfo.fetchInMs,
                        },
                    },
                },
            },
        };
    }

    private _setFetchArea(
        state: State,
        query: McQuery,
        conditionKey: string,
    ): State {
        const fetchInMs = Date.now();
        const condition = state.conditions[conditionKey] ?? {};

        return {
            ...state,
            conditions: {
                ...state.conditions,
                [conditionKey]: {
                    ...condition,
                    id: conditionKey,
                    MciAreaResponseInfo: McResponseMciSchema.parse({
                        ...condition.MciAreaResponseInfo,
                        fetchInMs,
                        fetch: true,
                        direction: query.direction,
                        type: query.geoType,
                        equipmentType: query.equipmentType,
                        timeframe: query.timeframe,
                    }),
                },
            },
        };
    }

    private _setLoadedArea(state: State, conditionKey: string): State {
        const loadedInMs = Date.now();
        const condition = state.conditions[conditionKey] ?? {};
        const responseInfo = condition.MciAreaResponseInfo ?? {};
        const durationInMs = !isNaN(loadedInMs - responseInfo.fetchInMs)
            ? loadedInMs - responseInfo.fetchInMs
            : -1;

        return {
            ...state,
            conditions: {
                ...state.conditions,
                [conditionKey]: {
                    ...condition,
                    id: conditionKey,
                    MciAreaResponseInfo: McResponseMciSchema.parse({
                        ...responseInfo,
                        loadedInMs,
                        loaded: true,
                        durationInMs,
                    } as McResponseMci),
                },
            },
        };
    }
    //#endregion
}
